/**
 * Database Audit Dashboard
 * Comprehensive view of all data in the database - eliminates confusion about what's actually stored
 */

import { useEffect, useState } from 'react'
import { Database, RefreshCw, Table, Calendar, Package, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import GlobalHeader from '../components/GlobalHeader'

interface RetailerData {
  retailer_id: string
  retailer_name: string
  total_records: number
  unique_weeks: number
  date_range: {
    oldest: { week: string; date: string }
    newest: { week: string; date: string }
  }
}

interface WeekData {
  fiscal_week: string
  period_week_label: string
  week_start: string
  week_end: string
  records: number
  products: number
}

interface AuditData {
  audit_timestamp: string
  database_info: {
    schemas: string[]
    total_tables: number
    tables_by_schema: Record<string, Array<{ name: string; size: string }>>
  }
  core_tables: {
    sku_detail_current: {
      description: string
      retailers: RetailerData[]
      week_by_week_breakdown: Record<string, WeekData[]>
    }
    store_sales_weekly: {
      description: string
      retailers: Array<{
        retailer_name: string
        total_records: number
        unique_weeks: number
        unique_stores: number
        week_range: string
      }>
    }
  }
  other_channels: Record<string, any>
  data_load_history: Record<string, Array<{
    fiscal_week: string
    loaded_at: string
    records: number
  }>>
  summary: {
    total_sku_records: number
    total_store_records: number
    retailers_with_data: number
    date_ranges: Array<{
      retailer: string
      weeks: number
      from: string
      to: string
    }>
  }
}

export default function DatabaseAudit() {
  const [data, setData] = useState<AuditData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRetailers, setExpandedRetailers] = useState<Set<string>>(new Set())

  const toggleRetailer = (retailerName: string) => {
    const newExpanded = new Set(expandedRetailers)
    if (newExpanded.has(retailerName)) {
      newExpanded.delete(retailerName)
    } else {
      newExpanded.add(retailerName)
    }
    setExpandedRetailers(newExpanded)
  }

  const fetchAuditData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/database-audit')
      if (!response.ok) {
        throw new Error('Failed to fetch database audit')
      }
      const auditData = await response.json()
      setData(auditData)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAuditData()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <GlobalHeader
          title="Database Audit"
          subtitle="Complete view of all data in the system"
          isLoading={true}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-6 w-32 bg-gray-200 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-20 w-full bg-gray-200 animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <GlobalHeader
          title="Database Audit"
          subtitle="Complete view of all data in the system"
          onRefresh={fetchAuditData}
        />
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Error loading audit data: {error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <div className="space-y-6">
      <GlobalHeader
        title="Database Audit"
        subtitle={`Last updated: ${new Date(data.audit_timestamp).toLocaleString()}`}
        onRefresh={fetchAuditData}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total SKU Records</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.total_sku_records.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Across {data.summary.retailers_with_data} retailers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Store Records</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.total_store_records.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Store-level sales data
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Database Schemas</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.database_info.schemas.length}</div>
            <p className="text-xs text-muted-foreground">
              {data.database_info.total_tables} tables total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Retailers</CardTitle>
            <Table className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.retailers_with_data}</div>
            <p className="text-xs text-muted-foreground">
              With historical data
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Retailer Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Retailer Data Summary</CardTitle>
          <CardDescription>
            Complete breakdown of data by retailer - THIS IS THE REAL DATA IN YOUR DATABASE
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.core_tables.sku_detail_current.retailers.map((retailer) => {
              const isExpanded = expandedRetailers.has(retailer.retailer_name)
              const weekData = data.core_tables.sku_detail_current.week_by_week_breakdown[retailer.retailer_name] || []
              
              return (
                <div key={retailer.retailer_id} className="border rounded-lg">
                  <div
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRetailer(retailer.retailer_name)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{retailer.retailer_name}</h3>
                        {isExpanded ? (
                          <span className="text-xs text-muted-foreground">▼ Click to collapse</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">▶ Click to see week-by-week</span>
                        )}
                      </div>
                      <Badge variant="outline">{retailer.total_records.toLocaleString()} records</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Fiscal Weeks</p>
                        <p className="font-medium">{retailer.unique_weeks} weeks</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Date Range Start</p>
                        <p className="font-medium">
                          {retailer.date_range.oldest.date} (FW {retailer.date_range.oldest.week})
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Date Range End</p>
                        <p className="font-medium">
                          {retailer.date_range.newest.date} (FW {retailer.date_range.newest.week})
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Week-by-week breakdown - collapsed by default */}
                  {isExpanded && weekData.length > 0 && (
                    <div className="border-t p-4 bg-gray-50">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Week-by-Week Breakdown (Last 100 weeks)
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="border-b">
                            <tr className="text-left">
                              <th className="pb-2 font-medium">Fiscal Week</th>
                              <th className="pb-2 font-medium">Period Label</th>
                              <th className="pb-2 font-medium">Week Start</th>
                              <th className="pb-2 font-medium">Week End</th>
                              <th className="pb-2 font-medium text-right">Records</th>
                              <th className="pb-2 font-medium text-right">Products</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {weekData.slice(0, 50).map((week, idx) => (
                              <tr key={idx} className="hover:bg-white">
                                <td className="py-2 font-mono">{week.fiscal_week}</td>
                                <td className="py-2">{week.period_week_label || '-'}</td>
                                <td className="py-2">{week.week_start || '-'}</td>
                                <td className="py-2">{week.week_end || '-'}</td>
                                <td className="py-2 text-right font-medium">{week.records.toLocaleString()}</td>
                                <td className="py-2 text-right">{week.products.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Database Schema Info */}
      <Card>
        <CardHeader>
          <CardTitle>Database Structure</CardTitle>
          <CardDescription>All schemas and tables in the database</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(data.database_info.tables_by_schema).map(([schema, tables]) => (
              <div key={schema} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">
                  Schema: {schema}
                  <Badge variant="secondary" className="ml-2">{tables.length} tables</Badge>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {tables.map((table: any) => (
                    <div key={table.name} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="font-mono text-xs">{table.name}</span>
                      <span className="text-xs text-muted-foreground">{table.size}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* DTC & Marketing Channels */}
      {Object.keys(data.other_channels).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>DTC & Marketing Channels</CardTitle>
            <CardDescription>
              Direct-to-consumer platforms (Amazon, Shopify, Walmart) and advertising data (Meta Ads, Google Analytics, TikTok)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.other_channels).map(([channel, info]) => {
                // Format channel name nicely
                const channelName = channel
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
                
                // Render marketing platforms
                if (channel === 'dtc_ad_credentials' && info.platforms) {
                  return (
                    <div key={channel} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-sm">Marketing Platform Credentials</h4>
                        <Badge variant="secondary">{info.total_platforms} platforms</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {info.platforms.map((platform: any) => (
                          <div key={platform.platform} className="flex items-center justify-between p-2 bg-white rounded border border-purple-200">
                            <span className="text-xs font-medium">{platform.platform}</span>
                            <Badge variant={platform.active > 0 ? 'default' : 'secondary'} className="text-xs">
                              {platform.active}/{platform.total}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }

                // Render campaign performance
                if (channel === 'dtc_campaign_performance' && info.platforms) {
                  return (
                    <div key={channel} className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-sm">Ad Campaign Performance</h4>
                        <Badge variant="secondary">{info.total_records.toLocaleString()} records</Badge>
                      </div>
                      <div className="space-y-2">
                        {info.platforms.map((platform: any) => (
                          <div key={platform.platform} className="flex items-center justify-between p-2 bg-white rounded border border-blue-200">
                            <div>
                              <span className="text-xs font-medium">{platform.platform}</span>
                              <p className="text-xs text-gray-500">{platform.date_range}</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="font-semibold text-green-600">${platform.total_spend}</p>
                              <p className="text-gray-600">{platform.total_conversions} conversions</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }

                // Render GA4 traffic
                if (channel === 'dtc_traffic_analytics') {
                  return (
                    <div key={channel} className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                        <h4 className="font-semibold text-sm">Google Analytics 4 Traffic</h4>
                        <Badge variant="secondary">{info.total_records.toLocaleString()} days</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 bg-white rounded border border-orange-200">
                          <p className="text-xs text-gray-600">Sessions</p>
                          <p className="text-sm font-bold">{parseInt(info.total_sessions || 0).toLocaleString()}</p>
                        </div>
                        <div className="p-2 bg-white rounded border border-orange-200">
                          <p className="text-xs text-gray-600">Users</p>
                          <p className="text-sm font-bold">{parseInt(info.total_users || 0).toLocaleString()}</p>
                        </div>
                        <div className="p-2 bg-white rounded border border-orange-200">
                          <p className="text-xs text-gray-600">Revenue</p>
                          <p className="text-sm font-bold text-green-600">${parseFloat(info.total_revenue || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">{info.date_range}</p>
                    </div>
                  )
                }

                // Render DTC sales summary (includes Walmart)
                if (channel === 'dtc_sales_summary' && info.platforms) {
                  return (
                    <div key={channel} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-sm">DTC Platform Sales</h4>
                        <Badge variant="secondary">{info.total_records.toLocaleString()} records</Badge>
                      </div>
                      <div className="space-y-2">
                        {info.platforms.map((platform: any) => (
                          <div key={platform.platform} className="flex items-center justify-between p-2 bg-white rounded border border-green-200">
                            <div>
                              <span className="text-xs font-medium">{platform.platform}</span>
                              <p className="text-xs text-gray-500">{platform.date_range}</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="font-semibold text-green-600">${parseFloat(platform.total_revenue).toLocaleString()}</p>
                              <p className="text-gray-600">{parseInt(platform.total_orders).toLocaleString()} orders</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }
                
                return (
                  <div key={channel} className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">{channelName}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Records</p>
                        <p className="font-medium">{info.total_records?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Date Range</p>
                        <p className="font-medium text-xs">{info.date_range}</p>
                      </div>
                      {info.unique_products && (
                        <div>
                          <p className="text-muted-foreground">Unique Products</p>
                          <p className="font-medium">{info.unique_products.toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

